import objectPath from 'object-path';
import { load } from 'recaptcha-v3';
import { ApolloClient } from '@apollo/client/core';
import { observable } from 'mobx';
import { InMemoryCache } from '@apollo/client/cache';
import { ApolloLink, HttpLink, from, split, fromPromise } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { RestLink } from 'apollo-link-rest';
import { WebSocketLink } from '@apollo/client/link/ws';
// import { SubscriptionClient } from 'subscriptions-transport-ws';
import { setContext } from '@apollo/client/link/context';

import { fromLocalStorage } from 'core/localStorage';
import { analytics } from 'core/analytics';
import { refresher, getJwt } from 'core/jwt';
import { ErrorCode } from 'core/errors';

import locationEndpoints from 'libs/location/api';
import uuid from 'uuid/v4';

const possibleTypes = {
  ContentInterface: [
    'ContentInterface',
    'Issue',
    'Initiative',
    'Profile',
    'Story',
    'Thread',
    'File',
    'Project',
    'Comment',
    'Contribution',
  ],
  NotificationTarget: [
    'Contribution',
    'Project',
    'Story',
    'Profile',
    'Thread',
    'Initiative',
    'Comment',
    'Issue',
    'GroupInvite',
    'HTMLNotification',
  ],
};

// observable for the status of the client
export const subscriptionStatusObservable = observable({
  status: 'initialized',
});

const apolloLinks = [];
let wsLink = null;
let wsHmacLink = null;
const subscriptionQueryLinks = [];

if (SUBSCRIPTION_SERVER_ENDPOINT) {
  wsLink = new WebSocketLink({
    uri: SUBSCRIPTION_SERVER_ENDPOINT,
    options: {
      reconnect: true,
      lazy: true,
      connectionParams: () => {
        const jwt = getJwt();
        if (jwt === 'null') {
          return {};
        }
        return jwt ? { Authorization: `Bearer ${jwt}` } : {};
      },
      connectionCallback: async error => {
        if (error) {
          if (error?.message?.includes('token')) {
            await refresher.refreshToken();
          }
        }
      },
    },
  });

  subscriptionQueryLinks.push(
    new HttpLink({
      uri: SUBSCRIPTION_SERVER_ENDPOINT.replace('ws', 'http'),
    }),
  );

  const { subscriptionClient } = wsLink;

  subscriptionClient.onConnected(() => {
    subscriptionStatusObservable.status = 'connected';
  });

  subscriptionClient.onReconnected(() => {
    subscriptionStatusObservable.status = 'connected';
  });

  subscriptionClient.onDisconnected(() => {
    subscriptionStatusObservable.status = 'disconnected';
  });
}

export const encodeUsername = username =>
  btoa(unescape(encodeURIComponent(username)));

export const authFetch = (url, args = {}) => {
  let username = '';
  const { HMAC } = args;
  try {
    let profileObject = args.signedInProfile;
    if (!profileObject) {
      profileObject = fromLocalStorage('signedInProfile');
    }

    const decodedUsername = objectPath.get(profileObject, 'username');
    if (decodedUsername && typeof decodedUsername === 'string') {
      username = encodeUsername(profileObject.username.slice(1));
    }
  } catch (error) {
    analytics.logError(error);
  }
  if (username) {
    const oldHeaders = args.headers;
    args.headers = {
      ...oldHeaders,
      'CLIENT-USERNAME': username,
    };
  }
  const jwt = getJwt();
  if (jwt && jwt !== 'null') {
    const oldHeaders = args.headers;
    args.headers = {
      ...oldHeaders,
      Authorization: `Bearer ${jwt}`,
    };
  }
  if (!username && !jwt && HMAC) {
    const oldHeaders = args.headers;
    args.headers = {
      ...oldHeaders,
      'X-Wikifactory-Hmac': HMAC,
    };
  }
  return fetch(url, args);
};

const jwtMiddleware = onError(
  // eslint-disable-next-line consistent-return
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        if (
          err.extensions &&
          err.extensions?.code === ErrorCode.INVALID_TOKEN
        ) {
          return fromPromise(
            refresher.refreshToken().then(token => {
              const oldHeaders = operation.getContext().headers;
              operation.setContext({
                headers: {
                  ...oldHeaders,
                  Authorization: token ? `Bearer ${token}` : '',
                },
              });
              return forward(operation);
            }),
          );
        }
      }
    }

    if (networkError) {
      console.error(`[Network error]: ${networkError}`);
      return forward(operation);
    }
  },
);

apolloLinks.push(jwtMiddleware);
subscriptionQueryLinks.unshift(jwtMiddleware);

const authMiddleware = new ApolloLink((operation, forward) => {
  let username = '';
  try {
    const profileObject = fromLocalStorage('signedInProfile');
    const decodedUsername = objectPath.get(profileObject, 'username');
    if (decodedUsername && typeof decodedUsername === 'string') {
      username = encodeUsername(profileObject.username.slice(1));
    }
  } catch (error) {
    analytics.logError(error);
  }
  if (username) {
    const oldHeaders = operation.getContext().headers;
    const context = {
      headers: {
        ...oldHeaders,
        'CLIENT-USERNAME': username,
      },
    };
    const jwt = getJwt();
    if (jwt && jwt !== 'null') {
      context.headers.Authorization = `Bearer ${jwt}`;
    }
    operation.setContext(context);
  }

  return forward(operation);
});

apolloLinks.push(authMiddleware);
subscriptionQueryLinks.unshift(authMiddleware);

class Recaptcha {
  async loadRecaptcha() {
    // NEED API TOKEN PAIR FOR ALL MICROSITES
    // Consider Language codes for internationalisation - if we use recaptcha 2 for sign-up
    if (!this.recaptcha) {
      this.recaptcha = await load(RECAPTCHA_SITE_KEY, {
        autoHideBadge: true,
        useRecaptchaNet: true,
      });
    }
    return this.recaptcha;
  }
}

if (RECAPTCHA_SITE_KEY) {
  const recaptchaStore = new Recaptcha();

  const recaptchaLink = setContext(async (operation, { headers }) => {
    const gqlType = getMainDefinition(operation.query).operation;
    if (gqlType === 'mutation') {
      const recaptcha = await recaptchaStore.loadRecaptcha();
      // Restrict to only certain mutations?
      // Use mutation name for 'action' - or group actions into broader types?
      const recaptchaToken = await recaptcha.execute('mutate');
      return {
        headers: {
          ...headers,
          'RECAPTCHA-TOKEN': recaptchaToken,
        },
      };
    }
    return { headers };
  });

  apolloLinks.push(recaptchaLink);
}

const namedLink = new ApolloLink((operation, forward) => {
  operation.setContext(() => ({
    uri: `${API_URL_BASE}/graphql?${operation.operationName}`,
  }));
  return forward ? forward(operation) : null;
});

apolloLinks.push(namedLink);

const inflights = {};

const abortableLink = new ApolloLink((operation, forward) => {
  // make using this link configurable by query?
  const requestId = uuid();

  const controller = new AbortController();
  const origContext = operation.getContext();
  if (origContext.abortOnRerequest) {
    operation.setContext({
      ...origContext,
      controller,
      fetchOptions: {
        signal: controller.signal,
      },
    });
    Object.keys(inflights).forEach(key => {
      if (key.startsWith(operation.operationName)) {
        inflights[key][0].abort();
        delete inflights[key];
      }
    });
    inflights[`${operation.operationName}-${requestId}`] = [
      controller,
      operation,
    ];
  }
  return forward(operation);
});

apolloLinks.push(abortableLink);

if (locationEndpoints) {
  const restLink = new RestLink({ endpoints: locationEndpoints });
  apolloLinks.push(restLink);
}

const httpLink = new HttpLink({
  uri: `${API_URL_BASE}/graphql`,
  credentials: 'include',
});

apolloLinks.push(httpLink);

const cache = new InMemoryCache({ possibleTypes });

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'none',
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'none',
  },
  mutate: {
    errorPolicy: 'none', // raise sysErrors on graphql errors
    // fetchPolicy: 'no-cache'
  },
};

const linkWithWs = split(
  // split based on operation type, send subscriptions through websocket link
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.name.value === 'ActiveParticipantStatus';
    },
    from(subscriptionQueryLinks),
    from(apolloLinks),
  ),
);

export const client = new ApolloClient({
  link: from(apolloLinks),
  cache,
  defaultOptions,
  dataIdFromObject: result => {
    if (result.id !== undefined) {
      return `${result.id}`;
    }
    return null;
  },
  connectToDevTools: CI,
});

export const initWebsocket = () => {
  wsHmacLink?.subscriptionClient.close();

  if (wsLink.subscriptionClient.status === 1) return;
  // just in case
  wsLink.subscriptionClient.close();

  client.setLink(linkWithWs);
  wsLink.subscriptionClient.connect();
};

export const initHMACWebsocket = HMAC => {
  // just in case of some race condition, we don't need to setup an HMAC subscription if we have a jwt.
  if (localStorage.getItem('jwt')) return;
  wsHmacLink = new WebSocketLink({
    uri: SUBSCRIPTION_SERVER_ENDPOINT,
    options: {
      reconnect: true,
      lazy: false,
      connectionParams: () => {
        return { HMAC };
      },
      // connectionCallback: async error => {},
    },
  });
  const linkWithHmacWs = split(
    // split based on operation type, send subscriptions through websocket link
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsHmacLink,
    from(apolloLinks),
  );
  client.setLink(linkWithHmacWs);
  // wsHmacLink.subscriptionClient.connect();
};

export const disconnectWebsocket = () => {
  wsLink.subscriptionClient.close();
};
